import { useEffect, useMemo } from "react";
import {
  BUILDING_DEFS,
  UNIT_DEFS,
  getCore,
  type Building,
  type BuildingType,
  type Lane,
  type LaunchMode,
  type Priority,
  type ResourceKind,
  type UnitType,
} from "../game";
import { useGameStore } from "../store/gameStore";

const BUILD_ORDER: BuildingType[] = ["veinway", "fossilRig", "amberTap", "alloyForge", "geneVat", "dynamo", "incubator", "spineTurret"];
const GLYPHS: Record<BuildingType, string> = {
  heartforge: "◆", veinway: "═", fossilRig: "⛏", amberTap: "◉", alloyForge: "▰", geneVat: "⌬", dynamo: "ϟ", incubator: "⬡", spineTurret: "⋔",
};
const RESOURCE_LABELS: Record<ResourceKind, string> = { fossil: "Fossil", amber: "Amber", alloy: "Alloy", genome: "Genome" };

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const remainder = Math.floor(seconds % 60).toString().padStart(2, "0");
  return `${minutes}:${remainder}`;
}

function formatCost(cost: Partial<Record<ResourceKind, number>>) {
  const short: Record<ResourceKind, string> = { fossil: "F", amber: "AM", alloy: "AL", genome: "G" };
  return (Object.entries(cost) as Array<[ResourceKind, number]>).map(([kind, amount]) => `${amount}${short[kind]}`).join(" · ") || "FREE";
}

function canAfford(stock: Record<ResourceKind, number>, cost: Partial<Record<ResourceKind, number>>) {
  return (Object.entries(cost) as Array<[ResourceKind, number]>).every(([kind, amount]) => stock[kind] >= amount);
}

function ResourceStrip() {
  const stock = useGameStore((state) => state.game.players.player.stock);
  return (
    <div className="resource-strip">
      {(Object.keys(RESOURCE_LABELS) as ResourceKind[]).map((kind) => (
        <div className={`resource ${kind}`} key={kind}>
          <span className="resource-label"><i className="resource-dot" />{RESOURCE_LABELS[kind]}</span>
          <span className="resource-value">{Math.floor(stock[kind])}</span>
        </div>
      ))}
    </div>
  );
}

function ObjectiveChip() {
  const game = useGameStore((state) => state.game);
  const playerCore = getCore(game, "player");
  const enemyCore = getCore(game, "ai");
  const playerBuildings = game.buildings.filter((building) => building.owner === "player" && !building.destroyed);
  const hasRaw = playerBuildings.some((building) => building.type === "fossilRig") && playerBuildings.some((building) => building.type === "amberTap");
  const hasRefining = playerBuildings.some((building) => building.type === "alloyForge") && playerBuildings.some((building) => building.type === "geneVat");
  const hasIncubator = playerBuildings.some((building) => building.type === "incubator");
  const objective = !hasRaw
    ? ["Connect the near deposits", "Veinways must form an unbroken line from your Heartforge."]
    : !hasRefining
      ? ["Refine combat matter", "Build an Alloy Forge and Gene Vat on the powered network."]
      : !hasIncubator
        ? ["Grow a war brood", "Add a Dynamo, then place and configure an Incubator."]
        : ["Rupture the rival Heartforge", "Tune recipes, squad size, and lane. Ramhorns excel at structures."];
  return (
    <div className="objective-chip">
      <span className="eyebrow">Current doctrine</span>
      <strong>{objective[0]}</strong>
      <p>{objective[1]}</p>
      <div className="core-meter">
        <div className="core-meter-label"><span>Your Heartforge</span><span>{Math.ceil(playerCore?.hp ?? 0)} HP</span></div>
        <div className="meter-track"><div className="meter-fill" style={{ "--meter": `${Math.max(0, ((playerCore?.hp ?? 0) / (playerCore?.maxHp ?? 1)) * 100)}%` } as React.CSSProperties} /></div>
      </div>
      <div className="core-meter enemy">
        <div className="core-meter-label"><span>Rival Heartforge</span><span>{Math.ceil(enemyCore?.hp ?? 0)} HP</span></div>
        <div className="meter-track"><div className="meter-fill" style={{ "--meter": `${Math.max(0, ((enemyCore?.hp ?? 0) / (enemyCore?.maxHp ?? 1)) * 100)}%` } as React.CSSProperties} /></div>
      </div>
    </div>
  );
}

function BuildDock() {
  const stock = useGameStore((state) => state.game.players.player.stock);
  const buildTool = useGameStore((state) => state.buildTool);
  const choose = useGameStore((state) => state.chooseBuildTool);
  const notify = useGameStore((state) => state.notify);
  return (
    <div className="build-dock" data-interactive="true" aria-label="Build structures">
      {BUILD_ORDER.map((type, index) => {
        const definition = BUILDING_DEFS[type];
        const affordable = canAfford(stock, definition.cost);
        return (
          <button
            key={type}
            type="button"
            className={`build-card ${buildTool === type ? "active" : ""} ${!affordable ? "disabled" : ""}`}
            onClick={() => affordable ? choose(buildTool === type ? null : type) : notify("Insufficient stock", `Need ${formatCost(definition.cost)} for ${definition.label}.`)}
            title={`${definition.label}: ${definition.description}`}
            aria-pressed={buildTool === type}
          >
            <span className="build-card-key">{index + 1}</span>
            <span className="build-glyph">{GLYPHS[type]}</span>
            <span className="build-name">{definition.label.replace("Pulse ", "").replace("War ", "")}</span>
            <span className="build-cost">{formatCost(definition.cost)}</span>
          </button>
        );
      })}
    </div>
  );
}

function Segmented<T extends string>({ values, value, labels, onChange }: { values: readonly T[]; value: T; labels?: Partial<Record<T, string>>; onChange: (value: T) => void }) {
  return (
    <div className="segmented" style={{ "--count": values.length } as React.CSSProperties}>
      {values.map((option) => <button type="button" key={option} className={value === option ? "active" : ""} onClick={() => onChange(option)}>{labels?.[option] ?? option}</button>)}
    </div>
  );
}

function IncubatorControls({ building }: { building: Building }) {
  const setRecipe = useGameStore((state) => state.setRecipe);
  const setLane = useGameStore((state) => state.setLane);
  const setMode = useGameStore((state) => state.setMode);
  const launch = useGameStore((state) => state.launchSelected);
  const recipes = ["clawpack", "shellback", "ramhorn"] as const;
  const lanes = ["north", "center", "south"] as const;
  const modes = ["drip", "pack3", "swarm6", "hold"] as const;
  const recipe = building.recipe ?? "clawpack";
  return (
    <>
      <div className="section-label">Genome recipe</div>
      <Segmented<UnitType> values={recipes} value={recipe} labels={{ clawpack: "Claw", shellback: "Shell", ramhorn: "Ram" }} onChange={setRecipe} />
      <div className="stat-row"><span>Cost / gestation</span><strong>{formatCost(UNIT_DEFS[recipe].cost)} · {UNIT_DEFS[recipe].buildTime}s</strong></div>
      <div className="section-label">Attack lane</div>
      <Segmented<Lane> values={lanes} value={building.lane} labels={{ north: "North", center: "Mid", south: "South" }} onChange={setLane} />
      <div className="section-label">Launch doctrine</div>
      <Segmented<LaunchMode> values={modes} value={building.launchMode} labels={{ drip: "Drip", pack3: "Pack 3", swarm6: "Swarm 6", hold: "Hold" }} onChange={setMode} />
      <div className="section-label">Garrison · {building.garrison.length}/6</div>
      <div className="queue-pips">
        {building.garrison.length ? building.garrison.map((unit, index) => <span className="queue-pip" title={UNIT_DEFS[unit].label} key={`${unit}-${index}`} />) : <span className="empty-copy">Brood chamber empty</span>}
      </div>
      <button type="button" className="action-button primary" onClick={launch}>Release garrison now</button>
    </>
  );
}

function Inspector() {
  const game = useGameStore((state) => state.game);
  const selectedId = useGameStore((state) => state.selectedId);
  const selectEntity = useGameStore((state) => state.selectEntity);
  const setPriority = useGameStore((state) => state.setSupplyPriority);
  const togglePaused = useGameStore((state) => state.toggleSelectedPaused);
  const sell = useGameStore((state) => state.sellSelected);
  const building = game.buildings.find((item) => item.id === selectedId && !item.destroyed);
  if (!building) return null;
  const definition = BUILDING_DEFS[building.type];
  const isPlayer = building.owner === "player";
  const priorities = ["reserve", "normal", "urgent"] as const;
  const manageable = !["heartforge", "veinway", "dynamo"].includes(building.type);
  return (
    <aside className="inspector" data-interactive="true">
      <div className="inspector-head">
        <div><span className="eyebrow">{isPlayer ? "Selected system" : "Rival intelligence"}</span><h2>{definition.label}</h2></div>
        <button className="icon-button" type="button" onClick={() => selectEntity(null)} aria-label="Close inspector">×</button>
      </div>
      <p className="inspector-copy">{definition.description}</p>
      <div className="stat-row"><span>Integrity</span><strong>{Math.ceil(building.hp)} / {building.maxHp}</strong></div>
      <div className="stat-row"><span>Network</span><strong>{building.connected ? "Linked" : "Severed"}</strong></div>
      <div className="stat-row"><span>Power</span><strong>{definition.powerUse ? `${building.powered ? "Online" : "Offline"} · ${definition.powerUse}` : definition.powerOutput ? `+${definition.powerOutput} capacity` : "Passive"}</strong></div>
      {isPlayer && manageable && (
        <>
          <div className="section-label">Supply priority</div>
          <Segmented<Priority> values={priorities} value={building.priority} labels={{ reserve: "Reserve", normal: "Normal", urgent: "Urgent" }} onChange={setPriority} />
        </>
      )}
      {isPlayer && building.type === "incubator" && <IncubatorControls building={building} />}
      {isPlayer && manageable && <button type="button" className="action-button" onClick={togglePaused}>{building.paused ? "Resume machinery" : "Pause machinery"}</button>}
      {isPlayer && building.type !== "heartforge" && <button type="button" className="action-button danger" onClick={sell}>Reclaim · 50% refund</button>}
    </aside>
  );
}

function IntroModal() {
  const started = useGameStore((state) => state.started);
  const start = useGameStore((state) => state.start);
  if (started) return null;
  return (
    <div className="modal-layer">
      <section className="modal">
        <span className="eyebrow">Fossil-tech supply skirmish // 1v1 AI</span>
        <h1>Dino<span>Conflict</span><br />Amberline</h1>
        <div className="tagline">Build the beast. Feed the front.</div>
        <div className="modal-grid">
          <div className="modal-fact"><strong>1 · Draw the line</strong><span>Connect Veinways from the Heartforge to Fossil and Amber deposits.</span></div>
          <div className="modal-fact"><strong>2 · Feed the brood</strong><span>Refine Alloy and Genome, then configure an Incubator’s recipe and lane.</span></div>
          <div className="modal-fact"><strong>3 · Break the heart</strong><span>Automated warbeasts fight the rival network. Ramhorns crush structures.</span></div>
        </div>
        <button type="button" className="launch-button" onClick={start}>Ignite Heartforge</button>
      </section>
    </div>
  );
}

function PauseAndHelp() {
  const paused = useGameStore((state) => state.paused);
  const helpOpen = useGameStore((state) => state.helpOpen);
  const togglePause = useGameStore((state) => state.togglePause);
  const setHelp = useGameStore((state) => state.setHelpOpen);
  const restart = useGameStore((state) => state.restart);
  if (!paused && !helpOpen) return null;
  return (
    <div className="modal-layer">
      <section className="modal">
        <span className="eyebrow">{helpOpen ? "Heartforge field manual" : "Simulation suspended"}</span>
        <h1 style={{ fontSize: "clamp(38px, 7vw, 64px)" }}>{helpOpen ? "Supply Doctrine" : "Paused"}</h1>
        <div className="modal-grid">
          <div className="modal-fact"><strong>Camera</strong><span>WASD pan · drag to move · right-drag orbit · wheel zoom.</span></div>
          <div className="modal-fact"><strong>Build</strong><span>1–8 choose structure · left-click place · right-click or Esc cancels.</span></div>
          <div className="modal-fact"><strong>Management</strong><span>Select buildings to set priority, recipe, lane, squad size, or pause.</span></div>
        </div>
        <p className="inspector-copy">Supply order is Urgent → Normal → Reserve. <strong>Tab</strong> reveals the connected flow graph. <strong>Space</strong> pauses. <strong>Delete</strong> reclaims the selected structure.</p>
        <button type="button" className="launch-button" onClick={() => { if (helpOpen) setHelp(false); else togglePause(); }}>Return to line</button>
        <button type="button" className="secondary-button" onClick={restart}>Restart match</button>
      </section>
    </div>
  );
}

function EndModal() {
  const game = useGameStore((state) => state.game);
  const restart = useGameStore((state) => state.restart);
  if (game.phase === "playing") return null;
  const victory = game.phase === "won";
  const player = game.players.player;
  return (
    <div className="modal-layer">
      <section className="modal">
        <span className="eyebrow">Combat genome resolved</span>
        <h1 className={`end-title ${victory ? "victory" : "defeat"}`}>{victory ? "Rival Ruptured" : "Heartforge Lost"}</h1>
        <div className="tagline">{victory ? "The amberline belongs to your brood." : "Every broken line teaches the next beast."}</div>
        <div className="modal-grid">
          <div className="modal-fact"><strong>{formatTime(game.time)}</strong><span>Match duration</span></div>
          <div className="modal-fact"><strong>{player.unitsSpawned}</strong><span>Warbeasts grown</span></div>
          <div className="modal-fact"><strong>{player.unitsDestroyed}</strong><span>Enemy units broken</span></div>
        </div>
        <button type="button" className="launch-button" onClick={restart}>Forge another timeline</button>
      </section>
    </div>
  );
}

function KeyboardBindings() {
  const started = useGameStore((state) => state.started);
  const gamePhase = useGameStore((state) => state.game.phase);
  const helpOpen = useGameStore((state) => state.helpOpen);
  const buildTool = useGameStore((state) => state.buildTool);
  const choose = useGameStore((state) => state.chooseBuildTool);
  const pause = useGameStore((state) => state.togglePause);
  const supply = useGameStore((state) => state.toggleSupplyLens);
  const help = useGameStore((state) => state.setHelpOpen);
  const sell = useGameStore((state) => state.sellSelected);
  const mute = useGameStore((state) => state.toggleMuted);
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (!started || gamePhase !== "playing") return;
      if (event.key >= "1" && event.key <= "8") choose(BUILD_ORDER[Number(event.key) - 1]);
      if (event.key === "Escape") {
        if (helpOpen) help(false);
        else if (buildTool) choose(null);
        else pause();
      }
      if (event.code === "Space") { event.preventDefault(); pause(); }
      if (event.key === "Tab") { event.preventDefault(); supply(); }
      if (event.key === "Delete" || event.key === "Backspace") sell();
      if (event.key.toLowerCase() === "h") help(true);
      if (event.key.toLowerCase() === "m") mute();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [buildTool, choose, gamePhase, help, helpOpen, mute, pause, sell, started, supply]);
  useEffect(() => {
    document.body.style.cursor = buildTool ? "crosshair" : "default";
    return () => { document.body.style.cursor = "default"; };
  }, [buildTool]);
  return null;
}

export function Hud() {
  const game = useGameStore((state) => state.game);
  const started = useGameStore((state) => state.started);
  const paused = useGameStore((state) => state.paused);
  const muted = useGameStore((state) => state.muted);
  const supplyLens = useGameStore((state) => state.supplyLens);
  const buildTool = useGameStore((state) => state.buildTool);
  const toasts = useGameStore((state) => state.toasts);
  const togglePause = useGameStore((state) => state.togglePause);
  const toggleMuted = useGameStore((state) => state.toggleMuted);
  const toggleSupply = useGameStore((state) => state.toggleSupplyLens);
  const setHelp = useGameStore((state) => state.setHelpOpen);
  const player = game.players.player;
  const visibleToasts = useMemo(() => toasts.filter((toast) => toast.expiresAt > performance.now()), [toasts]);
  return (
    <div className="hud">
      {started && game.phase === "playing" && (
        <>
          <div className="top-strip">
            <div className="brand-chip"><strong>DINO//CONFLICT</strong><span>AMBERLINE</span></div>
            <ResourceStrip />
            <div className="match-chip"><span className="match-time">{formatTime(game.time)}</span><span className="power-label">Network power</span><span className={`power-value ${player.powerUsed > player.powerCapacity ? "over" : ""}`}>{player.powerUsed} / {player.powerCapacity}</span></div>
          </div>
          <ObjectiveChip />
          <BuildDock />
          <Inspector />
          <div className="corner-tools" data-interactive="true">
            <button type="button" className={supplyLens ? "active" : ""} onClick={toggleSupply} title="Supply lens (Tab)">⌁</button>
            <button type="button" onClick={toggleMuted} title="Toggle sound (M)">{muted ? "◌" : "◉"}</button>
            <button type="button" onClick={() => setHelp(true)} title="Field manual (H)">?</button>
            <button type="button" onClick={togglePause} title="Pause (Space)">{paused ? "▶" : "Ⅱ"}</button>
          </div>
          <div className="toast-stack">{visibleToasts.map((toast) => <div key={toast.id} className={`toast ${toast.enemy ? "enemy" : ""}`}><strong>{toast.title}</strong>{toast.text}</div>)}</div>
          <div className="hint-pill">{buildTool ? <><kbd>LMB</kbd> place · <kbd>RMB</kbd> cancel · consecutive placement armed</> : <><kbd>WASD</kbd> pan · <kbd>drag</kbd> move · <kbd>RMB</kbd> orbit · <kbd>Tab</kbd> supply lens</>}</div>
          {supplyLens && <div className="supply-legend"><strong>Supply lens online</strong>Cyan/coral beams mark connected networks.<br />Cargo only credits when it reaches a Heartforge.</div>}
        </>
      )}
      <KeyboardBindings />
      <IntroModal />
      <PauseAndHelp />
      <EndModal />
    </div>
  );
}
